import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Student,
  ExamItem,
  ExamResult,
  LandingPageConfig,
  InstallmentItem,
  Schedule,
  LearningFolder,
} from '../types/db';
import {
  BookOpen,
  Sparkles,
  Wallet,
  CalendarDays,
  ImageIcon,
  Loader2,
  X,
  CheckCircle,
  Send,
  ListOrdered,
  ChevronRight,
  Clock,
  Download,
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface StudentPortalProps {
  student: Student;
  branding: LandingPageConfig;
}

export const StudentPortal: React.FC<StudentPortalProps> = ({ student, branding: _branding }) => {
  const [activeTab, setActiveTab] = useState<'BELAJAR' | 'UJIAN' | 'KAS'>('BELAJAR');
  const [loading, setLoading] = useState(false);

  // Academic Data States
  const [localSchedules, setLocalSchedules] = useState<Schedule[]>([]);
  const [availableExams, setAvailableExams] = useState<ExamItem[]>([]);
  const [availableFolders, setAvailableFolders] = useState<LearningFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [myResults, setMyResults] = useState<ExamResult[]>([]);

  // Quiz States
  const [activeQuiz, setActiveQuiz] = useState<ExamItem | null>(null);
  const [answers, setAnswers] = useState<Record<number, string>>({});

  // Finance States
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [selectedInstallmentIndex, setSelectedInstallmentIndex] = useState<number | null>(null);
  const [payForm, setPayForm] = useState({
    amount: 0,
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [proofFile, setProofFile] = useState<File | null>(null);

  const fetchPortalData = useCallback(async () => {
    if (!student?.class_id) return;

    setLoading(true);
    try {
      // 1. Ambil Jadwal Kelas Spesifik
      const { data: sch } = await supabase
        .from('schedules')
        .select('*, class_room:classes(*)')
        .eq('class_id', student.class_id)
        .order('date', { ascending: true });
      if (sch) setLocalSchedules(sch);

      // 2. Ambil Ujian / Kuis: Berdasarkan class_id ATAU yang terikat via exam_id di schedules kelas ini
      const examIdsFromSchedules = (sch || []).map((s: any) => s.exam_id).filter(Boolean);

      let examQuery = supabase.from('exams').select('*').eq('status', 'PUBLISHED');
      
      if (examIdsFromSchedules.length > 0) {
        examQuery = examQuery.or(`class_id.eq.${student.class_id},id.in.(${examIdsFromSchedules.join(',')})`);
      } else {
        examQuery = examQuery.eq('class_id', student.class_id);
      }

      const { data: ex } = await examQuery;
      if (ex) {
        const uniqueExams = Array.from(new Map(ex.map((item: any) => [item.id, item])).values());
        setAvailableExams(uniqueExams as ExamItem[]);
      }

      // 3. Ambil Modul Belajar dari tabel 'learning_folders'
      const branchFilter = [student.branch_id, 'ALL'].filter(Boolean) as string[];
      const { data: foldersData } = await supabase
        .from('learning_folders')
        .select('*')
        .in('branch_id', branchFilter)
        .ilike('grade', `%${student.grade.trim()}%`);

      if (foldersData) {
        setAvailableFolders(foldersData);
      }

      // 4. Ambil Riwayat Nilai Siswa
      const { data: res } = await supabase
        .from('exam_results')
        .select('*')
        .eq('student_id', student.id);
      if (res) setMyResults(res);
    } catch (_error) {
      /* handled silently */
    } finally {
      setLoading(false);
    }
  }, [student]);

  useEffect(() => {
    fetchPortalData();
  }, [fetchPortalData]);

  const groupedFolders = useMemo(() => {
    return Object.entries(
      availableFolders.reduce((acc: Record<string, LearningFolder[]>, item) => {
        const folderKey = item.grade || 'Tidak Terklasifikasi';
        if (!acc[folderKey]) acc[folderKey] = [];
        acc[folderKey].push(item);
        return acc;
      }, {})
    ).map(([grade, modules]) => ({ grade, modules }));
  }, [availableFolders]);

  const handleSubmitQuiz = async () => {
    if (loading || !activeQuiz) return;
    if (!window.confirm('Kirim jawaban sekarang?')) return;

    setLoading(true);
    try {
      let correctCount = 0;
      activeQuiz.questions.forEach((q, idx) => {
        if (answers[idx] === q.correctAnswer) correctCount++;
      });
      const score = Math.round((correctCount / activeQuiz.questions.length) * 100);

      const { error } = await supabase.from('exam_results').insert({
        exam_id: activeQuiz.id,
        student_id: student.id,
        student_name: student.name,
        score: score,
        total_questions: activeQuiz.questions.length,
        correct_answers: correctCount,
        answers: answers,
        submitted_at: new Date().toISOString(),
      });

      if (error) throw error;
      window.alert(`Ujian Selesai! Skor: ${score}`);
      setActiveQuiz(null);
      fetchPortalData();
    } catch (e: any) {
      window.alert('Gagal kirim: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    if (payForm.amount <= 0 || !proofFile) return alert('Lengkapi data!');
    setLoading(true);
    try {
      const fileExt = proofFile.name.split('.').pop();
      const filePath = `payments/${student.id}_${Date.now()}.${fileExt}`;
      await supabase.storage.from('learning-files').upload(filePath, proofFile);

      await supabase.from('pending_payments').insert({
        student_id: student.id,
        student_name: student.name,
        branch_id: student.branch_id,
        amount: payForm.amount,
        date: payForm.date,
        method: 'TRANSFER',
        notes: payForm.notes,
        transfer_proof_path: filePath,
        status: 'PENDING',
        installment_index: selectedInstallmentIndex,
      });

      setIsPayModalOpen(false);
      window.alert('Pembayaran terkirim! Menunggu verifikasi.');
    } catch (e: any) {
      window.alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  const record = student.financial_record;
  const totalPaid =
    record?.payments?.reduce((acc: number, p: any) => acc + (Number(p.amount) || 0), 0) || 0;
  const debt = Math.max(0, (record?.total_cost || 0) - totalPaid);

  return (
    <div className="space-y-6 animate-fade-in pb-10 font-sans">
      <div className="bg-indigo-600 p-8 md:p-10 rounded-[3rem] text-white shadow-xl relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="text-3xl font-black uppercase tracking-tight leading-tight">
            Halo, {student.name}!
          </h1>
          <p className="text-indigo-100 text-[10px] font-bold uppercase tracking-widest mt-2">
            {student.class_room?.name || 'BELUM ADA KELAS'} • {student.grade} • Unit{' '}
            {student.branch_id}
          </p>
        </div>
        <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
      </div>

      <div className="flex gap-2 bg-white p-1.5 rounded-2xl border border-slate-100 w-fit shadow-sm overflow-x-auto">
        {['BELAJAR', 'UJIAN', 'KAS'].map((t: any) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === t ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-50'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {activeTab === 'BELAJAR' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
            <h2 className="font-black text-slate-800 text-sm mb-6 flex items-center gap-3">
              <CalendarDays className="w-5 h-5 text-indigo-600" /> Jadwal Kelas Saya
            </h2>
            <div className="space-y-3">
              {localSchedules.map((s) => (
                <div
                  key={s.id}
                  className="p-5 bg-slate-50 rounded-[1.5rem] flex justify-between items-center border border-slate-100 group hover:border-indigo-200 transition-all"
                >
                  <div>
                    <p className="font-black text-slate-900 uppercase text-xs">{s.subject}</p>
                    <p className="text-[10px] text-slate-400 font-bold uppercase mt-1 flex items-center gap-1.5">
                      <Clock className="w-3 h-3 text-indigo-400" /> {s.date} | {s.time} WIB
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[8px] font-black text-indigo-600 uppercase tracking-widest mb-1">
                      {s.teacher_name || s.teacher}
                    </p>
                    {s.student_material_url && (
                      <a
                        href={s.student_material_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex p-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700"
                      >
                        <Download className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {localSchedules.length === 0 && (
                <div className="py-20 text-center text-slate-300 font-bold uppercase text-[10px]">
                  Belum ada jadwal terbit
                </div>
              )}
            </div>
          </div>

          <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
            <h2 className="font-black text-slate-800 text-sm mb-6 flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-indigo-600" /> Modul Materi
            </h2>
            {selectedFolder ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                      Folder: {selectedFolder}
                    </p>
                    <p className="text-[9px] text-slate-400 uppercase font-bold mt-1">
                      {groupedFolders.find((group) => group.grade === selectedFolder)?.modules
                        .length || 0}{' '}
                      modul tersedia
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedFolder(null)}
                    className="text-[9px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-800"
                  >
                    KEMBALI KE KELAS
                  </button>
                </div>
                <div className="space-y-3">
                  {groupedFolders
                    .find((group) => group.grade === selectedFolder)
                    ?.modules.map((m) => (
                      <a
                        key={m.id}
                        href={m.folder_url || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="block p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-indigo-50 transition-all"
                      >
                        <p className="font-black text-slate-800 uppercase text-[10px]">{m.title}</p>
                        <p className="text-[8px] text-slate-400 font-bold uppercase mt-1">
                          {m.subject}
                        </p>
                      </a>
                    ))}
                </div>
              </div>
            ) : groupedFolders.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {groupedFolders.map((folder) => (
                  <button
                    key={folder.grade}
                    onClick={() => setSelectedFolder(folder.grade)}
                    className="group block rounded-[2rem] border border-slate-100 bg-slate-50 p-6 text-left shadow-sm hover:border-indigo-200 hover:bg-indigo-50 transition-all"
                  >
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                          Folder Kelas
                        </p>
                        <h3 className="mt-2 text-lg font-black text-slate-900 uppercase">
                          {folder.grade}
                        </h3>
                      </div>
                      <div className="w-12 h-12 rounded-3xl bg-indigo-600 text-white flex items-center justify-center shadow-md">
                        {folder.modules.length}
                      </div>
                    </div>
                    <p className="text-[9px] text-slate-400 uppercase tracking-[0.2em]">
                      Klik untuk lihat file modul di dalam folder.
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center text-slate-300 italic text-[10px]">
                Modul belum tersedia
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'UJIAN' && (
        <div className="space-y-6">
          {activeQuiz ? (
            <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-xl space-y-8 animate-fade-in">
              <div className="flex justify-between items-center border-b pb-6">
                <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight">
                  {activeQuiz.subject}
                </h3>
                <button
                  onClick={() => setActiveQuiz(null)}
                  className="p-2 text-slate-300 hover:text-rose-500"
                >
                  <X />
                </button>
              </div>
              <div className="space-y-10 max-h-[500px] overflow-y-auto pr-4 no-scrollbar">
                {activeQuiz.questions.map((q, idx) => (
                  <div key={idx} className="space-y-4">
                    <p className="font-bold text-slate-800 flex gap-4">
                      <span className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center shrink-0 font-black text-xs">
                        {idx + 1}
                      </span>{' '}
                      {q.question}
                    </p>

                    {/* Render Gambar Soal untuk Siswa */}
                    {q.image_url && (
                      <div className="ml-12 my-3 p-4 bg-slate-50 rounded-2xl border border-slate-100 max-w-md shadow-sm">
                        <img 
                          src={q.image_url} 
                          alt={`Ilustrasi Soal #${idx + 1}`} 
                          className="max-h-64 w-auto object-contain rounded-xl mx-auto" 
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-12">
                      {q.options.map((opt, oIdx) => (
                        <button
                          key={oIdx}
                          onClick={() => setAnswers({ ...answers, [idx]: opt })}
                          className={`p-4 rounded-2xl text-left text-xs font-bold border-2 transition-all ${answers[idx] === opt ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-slate-50 border-transparent hover:border-indigo-100'}`}
                        >
                          {String.fromCharCode(65 + oIdx)}. {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-8 border-t flex justify-end">
                <button
                  onClick={handleSubmitQuiz}
                  disabled={loading}
                  className="bg-emerald-600 text-white px-10 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl hover:bg-emerald-700 transition-all flex items-center gap-3"
                >
                  {loading ? (
                    <Loader2 className="animate-spin w-4 h-4" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}{' '}
                  KIRIM JAWABAN
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="font-black text-indigo-900 uppercase text-[10px] tracking-widest ml-1 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> Ujian & Daily Quiz Tersedia
                </h4>
                {availableExams.map((ex) => {
                  const hasTaken = myResults.some((r) => r.exam_id === ex.id);
                  return (
                    <div
                      key={ex.id}
                      className="p-6 bg-white border border-slate-100 rounded-[2rem] flex justify-between items-center shadow-sm group hover:border-indigo-200 transition-all"
                    >
                      <div>
                        <p className="font-black text-slate-900 uppercase text-sm">{ex.subject}</p>
                        <p className="text-[9px] text-slate-400 font-bold uppercase mt-1">
                          {ex.date} • {ex.questions.length} Soal
                        </p>
                      </div>
                      {hasTaken ? (
                        <div className="bg-emerald-50 text-emerald-600 px-4 py-2 rounded-xl font-black text-[8px] uppercase border border-emerald-100 flex items-center gap-2">
                          <CheckCircle className="w-3 h-3" /> SELESAI
                        </div>
                      ) : (
                        <button
                          onClick={() => setActiveQuiz(ex)}
                          className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-black text-[9px] uppercase shadow-lg hover:bg-indigo-700 transition-all flex items-center gap-2"
                        >
                          KERJAKAN <ChevronRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
                {availableExams.length === 0 && (
                  <div className="py-20 text-center text-slate-300 font-bold uppercase text-[10px]">
                    Ujian atau Daily Quiz belum tersedia
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <h4 className="font-black text-slate-400 uppercase text-[10px] tracking-widest ml-1 flex items-center gap-2">
                  <ListOrdered className="w-4 h-4" /> Nilai Saya
                </h4>
                <div className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-sm">
                  <table className="w-full text-[10px]">
                    <thead className="bg-slate-50 border-b text-slate-400 font-black uppercase tracking-widest">
                      <tr>
                        <th className="p-5 text-left">Nama Ujian / Kuis</th>
                        <th className="p-5 text-center">Skor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {myResults.map((r) => (
                        <tr key={r.id} className="font-bold">
                          <td className="p-5 text-slate-700 uppercase tracking-tight">
                            {availableExams.find((x) => x.id === r.exam_id)?.subject || 'UJIAN'}
                          </td>
                          <td className="p-5 text-center">
                            <span
                              className={`px-4 py-1.5 rounded-lg font-black ${r.score >= 75 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}
                            >
                              {r.score}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'KAS' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in">
          <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-sm text-center flex flex-col justify-center">
            <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-[2rem] flex items-center justify-center mx-auto mb-8 shadow-xl shadow-indigo-100">
              <Wallet className="w-10 h-10" />
            </div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
              Sisa Piutang Kursus
            </p>
            <h3
              className={`text-4xl font-black tracking-tighter ${debt > 0 ? 'text-rose-600' : 'text-emerald-600'}`}
            >
              Rp {debt.toLocaleString()}
            </h3>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <h4 className="font-black text-indigo-900 uppercase text-[11px] tracking-widest ml-2 flex items-center gap-2">
              <ListOrdered className="w-4 h-4" /> Rencana Angsuran
            </h4>
            <div className="bg-white rounded-[3rem] border border-slate-100 shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b text-slate-400 font-black uppercase tracking-widest text-[9px]">
                  <tr>
                    <th className="p-6 text-left">Deskripsi</th>
                    <th className="p-6 text-right">Nominal</th>
                    <th className="p-6 text-center">Status</th>
                    <th className="p-6 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {(student.financial_record?.installment_plan || []).map(
                    (item: InstallmentItem, idx: number) => (
                      <tr key={idx} className="font-bold text-slate-800">
                        <td className="p-6 uppercase">{item.label}</td>
                        <td className="p-6 text-right font-black">
                          Rp {item.amount.toLocaleString()}
                        </td>
                        <td className="p-6 text-center">
                          <span
                            className={`px-4 py-1.5 rounded-full text-[8px] font-black uppercase border tracking-widest ${item.status === 'PAID' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-400'}`}
                          >
                            {item.status === 'PAID' ? 'LUNAS' : 'MENUNGGU'}
                          </span>
                        </td>
                        <td className="p-6 text-center">
                          {item.status !== 'PAID' && (
                            <button
                              onClick={() => {
                                setSelectedInstallmentIndex(idx);
                                setPayForm({
                                  ...payForm,
                                  amount: item.amount,
                                  notes: 'Bayar ' + item.label,
                                });
                                setIsPayModalOpen(true);
                              }}
                              className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-black uppercase text-[8px] hover:bg-indigo-700 shadow-lg"
                            >
                              BAYAR
                            </button>
                          )}
                          {item.status === 'PAID' && (
                            <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {isPayModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[150] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white p-10 rounded-[3rem] w-full max-w-md space-y-8 shadow-2xl animate-fade-in-up border border-white relative">
            <button
              onClick={() => setIsPayModalOpen(false)}
              className="absolute top-8 right-8 text-slate-300 hover:text-slate-900"
            >
              <X />
            </button>
            <div className="text-center">
              <h3 className="font-black text-2xl uppercase tracking-tighter text-slate-900">
                Konfirmasi Bayar
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                Lampirkan Bukti Transfer Anda
              </p>
            </div>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2">
                  Nominal (Rp)
                </label>
                <input
                  type="number"
                  disabled
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black text-xl text-indigo-900 bg-slate-50/50"
                  value={payForm.amount}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black text-indigo-600 uppercase ml-2">
                  Keterangan
                </label>
                <input
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl font-bold bg-slate-50/50 text-[11px]"
                  value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                />
              </div>
              <label className="border-4 border-dashed border-slate-50 rounded-[2rem] p-10 flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-slate-50 transition-all text-slate-300">
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                />
                {proofFile ? (
                  <div className="text-center font-black text-indigo-600 uppercase text-[9px] truncate w-full">
                    {proofFile.name}
                  </div>
                ) : (
                  <ImageIcon className="w-10 h-10" />
                )}
                <span className="font-black text-[9px] uppercase tracking-widest text-center">
                  Tap untuk Upload Bukti
                </span>
              </label>
              <button
                onClick={handlePay}
                disabled={loading || !proofFile}
                className="w-full bg-indigo-600 text-white py-5 rounded-[2rem] font-black uppercase tracking-widest shadow-xl flex items-center justify-center gap-2 hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="animate-spin w-4 h-4" />
                ) : (
                  <Send className="w-4 h-4" />
                )}{' '}
                KIRIM KONFIRMASI
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
